// Icons for widget types and for the agents people connect.

import { AgentMark } from "./agentMarks";
import { Activity, BarChart3, Bookmark, Cable, SearchCheck, BellRing, Bot, Calendar, CircleGauge, Clock, Code2, Earth, FileText, Flame, Frame, GitCommitHorizontal, Globe2, Heading, Hourglass, Image, Link2, List, ListChecks, MousePointerClick, NotebookPen, PlayCircle, Radio, ScrollText, StickyNote, Sun, Sunrise, Table2, Target, Timer, TrendingUp, Zap } from "lucide-react";
import type { WidgetType } from "../../shared/types";

const MAP: Record<WidgetType, typeof Activity> = {
  metric: TrendingUp,
  chart: BarChart3,
  goal: Target,
  list: List,
  table: Table2,
  status: CircleGauge,
  feed: Activity,
  agent: Bot,
  map: Earth,
  brief: FileText,
  file: ScrollText,
  job: PlayCircle,
  action: MousePointerClick,
  needsyou: BellRing,
  usage: Zap,
  gitlog: GitCommitHorizontal,
  connections: Cable,
  custom: Code2,
  notes: NotebookPen,
  tasks: ListChecks,
  heading: Heading,
  sticky: StickyNote,
  links: Link2,
  clock: Clock,
  worldclock: Globe2,
  countdown: Hourglass,
  timer: Timer,
  radio: Radio,
  image: Image,
  embed: Frame,
  calendar: Calendar,
  weather: Sun,
  sun: Sunrise,
  habits: Flame,
  seo: SearchCheck,
  bookmarks: Bookmark,
};

export function WidgetIcon({ type, size = 16, strokeWidth = 1.75 }: { type: WidgetType; size?: number; strokeWidth?: number }) {
  const I = MAP[type] ?? Activity;
  return <I size={size} strokeWidth={strokeWidth} />;
}

// Simple marks for the tools in the Connect dialog. Drawn here, not fetched.
export function BrandMark({ id, size = 18 }: { id: string; size?: number }) {
  const s = size;
  switch (id) {
    // Claude's and OpenAI's own marks (agentMarks.tsx), the same wherever the board names them
    case "claude":
    case "claude-code":
      return <AgentMark kind="claude" size={s} />;
    case "chatgpt":
    case "codex":
      return <AgentMark kind="codex" size={s} />;
    // Cursor's own cube (agentMarks.tsx), as its agents wear it on the board (Fixes 9)
    case "cursor":
      return <AgentMark kind="cursor" size={s} />;
    case "windsurf":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden fill="none" stroke="#2fd3b5" strokeWidth="1.8" strokeLinecap="round">
          <path d="M3 15c3-4 6-4 9 0s6 4 9 0" />
          <path d="M3 9c3-4 6-4 9 0s6 4 9 0" opacity=".6" />
        </svg>
      );
    case "n8n":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden fill="none" stroke="#ea4b71" strokeWidth="1.8">
          <circle cx="5" cy="12" r="2.4" />
          <circle cx="12" cy="7" r="2.4" />
          <circle cx="19" cy="12" r="2.4" />
          <circle cx="12" cy="17" r="2.4" />
          <path d="M7.2 11 9.9 8.3M14.1 8.3 16.8 11M16.8 13 14.1 15.7M9.9 15.7 7.2 13" />
        </svg>
      );
    case "zapier":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden fill="#ff6a2b">
          {[0, 45, 90, 135].map((r) => (
            <rect key={r} x="10.9" y="3" width="2.2" height="18" rx="1.1" transform={`rotate(${r} 12 12)`} />
          ))}
        </svg>
      );
    case "make":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden fill="none" stroke="#a85ef0" strokeWidth="2.2" strokeLinecap="round">
          <path d="M5 18 9 6M11 18l4-12M17 18l2-6" />
        </svg>
      );
    case "terminal":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <rect x="3" y="4.5" width="18" height="15" rx="3" />
          <path d="m7.5 10 3 2.5-3 2.5M12.5 15H16" />
        </svg>
      );
    case "vscode":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden fill="none" stroke="#3ea6f5" strokeWidth="1.8" strokeLinejoin="round">
          <path d="M17 3.5 20.5 5v14L17 20.5 6.5 11 3.5 13.5 2.5 12l3-3L17 3.5Z" />
          <path d="M17 3.5v17M6.5 13 17 20.5" opacity=".55" />
        </svg>
      );
    case "other":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <rect x="3.5" y="3.5" width="7" height="7" rx="2" />
          <rect x="13.5" y="3.5" width="7" height="7" rx="2" />
          <rect x="3.5" y="13.5" width="7" height="7" rx="2" />
          <path d="M17 14v6M14 17h6" />
        </svg>
      );
    default:
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}
